import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeftIcon } from 'lucide-react';
import { PLATFORM_ROLES } from '@siteflow/shared';
import { LicenceStatusChip, humanise } from '@/components/admin/licence-status-chip';
import { ReasonDialog } from '@/components/admin/reason-dialog';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { SlotMeter } from '@/components/licence/slot-meter';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { adminApi, adminKeys, adminLicenceQuery } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { getAuthState } from '@/lib/auth-store';
import { formatDate, formatDateTime } from '@/lib/utils';

type Action = 'extend' | 'slots' | 'suspend' | 'revoke' | 'reinstate';

const LIVE = new Set(['active', 'trial']);
const RESTORABLE = new Set(['suspended', 'revoked', 'expired', 'exhausted']);

function List({ items, empty }: { items: Array<[string, string, string]>; empty: string }) {
  if (items.length === 0) return <p className="text-data text-ink-muted">{empty}</p>;
  return (
    <ul className="flex flex-col divide-y divide-hairline">
      {items.map(([key, left, right]) => (
        <li key={key} className="flex items-center justify-between gap-3 py-2 text-data">
          <span className="min-w-0 truncate">{left}</span>
          <span className="shrink-0 text-micro text-ink-subtle">{right}</span>
        </li>
      ))}
    </ul>
  );
}

function AdminLicencePage() {
  const { licenceId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery(adminLicenceQuery(licenceId));
  const isSuper = getAuthState().user?.platformRole === PLATFORM_ROLES.SUPER_ADMIN;

  const [action, setAction] = useState<Action | null>(null);
  const [days, setDays] = useState('30');
  const [quantity, setQuantity] = useState('1');

  const done = (message: string) => {
    toast.success(message);
    setAction(null);
    void queryClient.invalidateQueries({ queryKey: adminKeys.licence(licenceId) });
    void queryClient.invalidateQueries({ queryKey: [...adminKeys.all, 'licences'] });
  };

  const mutation = useMutation({
    mutationFn: async (reason: string) => {
      switch (action) {
        case 'extend':
          await adminApi.extendLicence(licenceId, { days: Number(days), reason });
          return 'Licence extended';
        case 'slots':
          await adminApi.grantSlots(licenceId, { quantity: Number(quantity), reason });
          return 'Slots updated';
        case 'suspend':
          await adminApi.overrideLicence(licenceId, { status: 'suspended', reason });
          return 'Licence suspended';
        case 'revoke':
          await adminApi.revokeLicence(licenceId, { reason });
          return 'Licence revoked';
        case 'reinstate':
          await adminApi.reinstateLicence(licenceId, { reason });
          return 'Licence reinstated';
        default:
          throw new Error('No action selected');
      }
    },
    onSuccess: done,
    onError: (error) => toast.error(messageForError(error)),
  });

  const back = (
    <Button variant="secondary" size="sm" asChild>
      <Link to="/admin/licences" search={{ page: 1 }}>
        <ArrowLeftIcon /> All licences
      </Link>
    </Button>
  );

  if (query.isPending) return <Skeleton className="h-96 w-full" />;
  if (query.isError) {
    return <EmptyState title="Licence not found" description={messageForError(query.error)} action={back} />;
  }

  const { licence, activations, validations, slotEvents, files, fingerprints } = query.data;
  const status = licence.status;
  const daysValid = Number.isInteger(Number(days)) && Number(days) >= 1 && Number(days) <= 3650;
  const quantityValid =
    Number.isInteger(Number(quantity)) && Number(quantity) !== 0 && Math.abs(Number(quantity)) <= 100;

  const copy: Record<Action, { title: string; description: string; confirm: string; destructive: boolean }> = {
    extend: {
      title: 'Extend licence',
      description: 'Adds days to the expiry. An expired licence becomes active again.',
      confirm: 'Extend',
      destructive: false,
    },
    slots: {
      title: 'Grant or remove slots',
      description: 'Positive grants production slots outside the subscription; negative removes them.',
      confirm: 'Apply',
      destructive: false,
    },
    suspend: {
      title: 'Suspend licence',
      description: 'Publishing blocks immediately. Public content keeps being served.',
      confirm: 'Suspend',
      destructive: true,
    },
    revoke: {
      title: 'Revoke licence',
      description: 'Revocation also blocks editing. This is for abuse, not non-payment.',
      confirm: 'Revoke',
      destructive: true,
    },
    reinstate: {
      title: 'Reinstate licence',
      description: 'Returns the licence to active.',
      confirm: 'Reinstate',
      destructive: false,
    },
  };

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Licence"
        title={`${licence.planKey} · ${humanise(licence.kind)}`}
        description={
          licence.expiresAt
            ? `Expires ${formatDate(licence.expiresAt)}${licence.graceUntil ? `, grace until ${formatDate(licence.graceUntil)}` : ''}`
            : 'No expiry'
        }
        actions={
          <>
            {back}
            <Button variant="secondary" size="sm" asChild>
              <Link to="/admin/customers/$organisationId" params={{ organisationId: licence.organisationId }}>
                Organisation
              </Link>
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle>State</CardTitle>
            <LicenceStatusChip status={status} />
          </div>
          <CardDescription>
            {isSuper
              ? 'Every action needs a reason and is written to the audit log.'
              : 'Read-only. Changing a licence needs the super administrator role.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <SlotMeter slots={licence.slots} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" disabled={!isSuper} onClick={() => setAction('extend')}>
              Extend
            </Button>
            <Button size="sm" variant="secondary" disabled={!isSuper} onClick={() => setAction('slots')}>
              Grant slots
            </Button>
            {LIVE.has(status) ? (
              <Button size="sm" variant="outlineDanger" disabled={!isSuper} onClick={() => setAction('suspend')}>
                Suspend
              </Button>
            ) : null}
            {status !== 'revoked' && status !== 'cancelled' ? (
              <Button size="sm" variant="outlineDanger" disabled={!isSuper} onClick={() => setAction('revoke')}>
                Revoke
              </Button>
            ) : null}
            {RESTORABLE.has(status) ? (
              <Button size="sm" disabled={!isSuper} onClick={() => setAction('reinstate')}>
                Reinstate
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Restrictions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-data">
            <p>
              <span className="text-ink-subtle">Domains: </span>
              {licence.allowedDomains.length ? licence.allowedDomains.join(', ') : 'any'}
            </p>
            <p>
              <span className="text-ink-subtle">IP allowlist: </span>
              {licence.allowedIps.length ? licence.allowedIps.join(', ') : 'none'}
            </p>
            <p>
              <span className="text-ink-subtle">Environments: </span>
              {licence.allowedEnvironments.join(', ') || 'all'}
            </p>
            <div className="flex flex-wrap gap-2">
              <StatusChip tone={licence.requireDomainVerification ? 'info' : 'neutral'} showDot={false}>
                Domain verification {licence.requireDomainVerification ? 'required' : 'optional'}
              </StatusChip>
              <StatusChip tone={licence.requireFingerprint ? 'info' : 'neutral'} showDot={false}>
                Fingerprint {licence.requireFingerprint ? 'bound' : 'not bound'}
              </StatusChip>
              <StatusChip tone={licence.offlineFilesEnabled ? 'info' : 'neutral'} showDot={false}>
                Offline files {licence.offlineFilesEnabled ? 'on' : 'off'}
              </StatusChip>
            </div>
            {licence.notes ? <p className="rounded-md bg-sunken p-3">{licence.notes}</p> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activations</CardTitle>
            <CardDescription>{activations.length} recorded</CardDescription>
          </CardHeader>
          <CardContent>
            <List
              empty="Nothing activated yet."
              items={activations.map((row) => [
                row.id,
                `${row.domain ?? row.instanceId ?? 'unknown'} · ${row.environmentType}${row.consumedSlot ? ' · uses slot' : ''}`,
                `${row.status} · ${formatDate(row.activatedAt)}`,
              ])}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Slot ledger</CardTitle>
            <CardDescription>Append-only history of every slot movement</CardDescription>
          </CardHeader>
          <CardContent>
            <List
              empty="No slot events."
              items={slotEvents.map((row) => [
                row.id,
                `${humanise(row.event)} (${row.delta > 0 ? '+' : ''}${row.delta}) → ${row.slotsUsedAfter} used${row.reason ? ` · ${row.reason}` : ''}`,
                formatDateTime(row.createdAt),
              ])}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Validation history</CardTitle>
            <CardDescription>Last 100 checks from running installations</CardDescription>
          </CardHeader>
          <CardContent>
            <List
              empty="No validations yet."
              items={validations.map((row) => [
                row.id,
                `${row.result}${row.reasonCode ? ` · ${row.reasonCode}` : ''}${row.domain ? ` · ${row.domain}` : ''}`,
                formatDateTime(row.createdAt),
              ])}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Offline files</CardTitle>
          </CardHeader>
          <CardContent>
            <List
              empty="No offline licence files issued."
              items={files.map((file) => [
                file.id,
                `Issued ${formatDate(file.issuedAt)} · ${file.downloadCount} download${file.downloadCount === 1 ? '' : 's'}`,
                file.revokedAt ? `Revoked ${formatDate(file.revokedAt)}` : 'Valid',
              ])}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Server fingerprints</CardTitle>
            <CardDescription>Hashed on arrival; only a short prefix is ever shown.</CardDescription>
          </CardHeader>
          <CardContent>
            <List
              empty="No fingerprints bound."
              items={fingerprints.map((row) => [
                row.id,
                `${row.hashPrefix}… · ${row.status}`,
                `Seen ${formatDate(row.lastSeenAt)}`,
              ])}
            />
          </CardContent>
        </Card>
      </div>

      {action ? (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setAction(null)}
          title={copy[action].title}
          description={copy[action].description}
          confirmLabel={copy[action].confirm}
          destructive={copy[action].destructive}
          pending={mutation.isPending}
          extraValid={action === 'extend' ? daysValid : action === 'slots' ? quantityValid : true}
          onConfirm={(reason) => mutation.mutate(reason)}
        >
          {action === 'extend' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="extend-days">Days to add</Label>
              <Input
                id="extend-days"
                inputMode="numeric"
                value={days}
                aria-invalid={!daysValid}
                onChange={(event) => setDays(event.target.value)}
              />
            </div>
          ) : null}
          {action === 'slots' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="slot-quantity">Slots (negative removes)</Label>
              <Input
                id="slot-quantity"
                inputMode="numeric"
                value={quantity}
                aria-invalid={!quantityValid}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
          ) : null}
        </ReasonDialog>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/licences/$licenceId')({
  component: AdminLicencePage,
});
